// src/pages/ShopReport/ShopReport.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import Filters from '../../components/Filters';
import ShopsDataGrid from '../../components/ShopsDataGrid';
import { Typography, Box, Button } from '@mui/material';
import BackButton from '../../components/BackButton';

/* ──────────────────────────────────────────────
   Map reportType → module ID (must match backend)
────────────────────────────────────────────── */
const MODULE_ID_BY_TYPE = {
  'total-collection' : 3910,
  'total-commissions': 3911,
  'transactions'     : 3912,
  'commission-split' : 3913,
  'commission-reset' : 3914,
  'welfare-amount'   : 3914, // adjust if needed
  'reduce-welfare'   : 3914, // adjust if needed
  'acemoney-transactions': 3915,
  'acemoney-settlements': 3916,
};

const ShopReport = () => {
  const { reportType } = useParams();
  const navigate = useNavigate();

  const [userLevel,   setUserLevel]   = useState(null);
  const [userId,      setUserId]      = useState(null);
  const [userModules, setUserModules] = useState([]); // numeric IDs
  const [privileges,  setPrivileges]  = useState({});
  const [privLoaded,  setPrivLoaded]  = useState(false); // 🔑 new
  const [shops,       setShops]       = useState([]);
  const [filters,     setFilters]     = useState({});
  const [loading,     setLoading]     = useState(false);
  const [moduleId,    setModuleId]    = useState(null);
  const [reportTitle, setReportTitle] = useState('');
  const [noShopsFound, setNoShopsFound] = useState(false);

  /* ──────────────────────────────────────────
     Decode token once
  ────────────────────────────────────────── */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const { level, userId: uid } = jwtDecode(token);
    setUserLevel(level);
    setUserId(uid);

    const id = MODULE_ID_BY_TYPE[reportType] || null;
    if (!id) { navigate('/unauthorized'); return; }

    setModuleId(id);

    if (level === 1 || level === 2) {
      setUserModules([id]);
      setPrivLoaded(true);
    } else {
      fetchPrivileges(uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  /* ──────────────────────────────────────────
     Fetch privileges & normalise to Number
  ────────────────────────────────────────── */
  const fetchPrivileges = async (uid) => {
    try {
      const { data, status } = await axios.get(`${API_BASE_URL}/privileges/${uid}`);
      if (status === 200) {
        setPrivileges(data);
        setUserModules(data.modules.map(m => Number(m.id || m)));
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
    } finally {
      setPrivLoaded(true);                          // 🔑 mark done
    }
  };

  /* ──────────────────────────────────────────
     Access helper
  ────────────────────────────────────────── */
  const hasAccess = () => {
    if (userLevel === 1 || userLevel === 2) return true;
    return (
      userModules.includes(moduleId) ||
      userModules.includes(String(moduleId))
    );
  };

  /* ──────────────────────────────────────────
     Run guard *after* privileges arrive
  ────────────────────────────────────────── */
  useEffect(() => {
    if (!moduleId) return;
    if (!privLoaded) return;                        // 🔑 wait
    if (!hasAccess()) {
      alert('You do not have access to this report');
      navigate('/unauthorized');
    } else {
      setReportTitle(getReportTitle(reportType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privLoaded, moduleId, userModules]);

  const getReportTitle = (type) => ({
    'total-collection' : 'Merchants Daily Report',
    'total-commissions': 'Total Commissions',
    'transactions'     : 'Transactions',
    'commission-split' : 'Commission Split',
    'welfare-amount'   : 'Welfare Amount',
    'reduce-welfare'   : 'Reduce Welfare',
    'commission-reset' : 'Commission Reset',
    'acemoney-transactions': 'AceMoney Transactions',
    'acemoney-settlements': 'AceMoney Settlements',
  }[type] || 'Report');

  const handleExportCSV = () => {
    if (!shops || shops.length === 0) return;
    const headers = ['Merchant ID', 'Merchant Name', 'Shop Name', 'District', 'City', 'Pincode', 'Mobile Number', 'Email'];
    const csvRows = [headers.join(',')];
    shops.forEach(s => {
      csvRows.push([
        `"${s.sellerIdentifier || s.shopId || s._id}"`,
        `"${s.sellerName || ''}"`,
        `"${s.businessName || ''}"`,
        `"${s.district || ''}"`,
        `"${s.city || ''}"`,
        `"${s.pincode || ''}"`,
        `"${s.mobileNumber || ''}"`,
        `"${s.emailId || ''}"`
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_Shops_Export.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  /* ──────────────────────────────────────────
     Fetch shops (unchanged)
  ────────────────────────────────────────── */
  useEffect(() => {
    if (userId !== null) fetchShops(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, userId]);

  const fetchShops = async (f) => {
    setLoading(true);
    try {
      const pageNumber = f.pageNumber || 1;
      const token = sessionStorage.getItem('token');

      const cfg = {
        headers: { Authorization: `Bearer ${token}` },
        params : { pageNumber },
      };

      let url = '';
      if      (f.pincode   ) { url = `${API_BASE_URL}/shops/by-pincode`;  cfg.params.pincode   = f.pincode; }
      else if (f.talukId   ) { url = `${API_BASE_URL}/shops/by-taluk`;    cfg.params.talukId   = f.talukId; }
      else if (f.districtId) { url = `${API_BASE_URL}/shops/by-district`; cfg.params.districtId= f.districtId; }
      else                   { url = `${API_BASE_URL}/shops/${userId}`; }

      const { data, status } = await axios.get(url, cfg);
      if (status === 200 && data.shops && data.shops.length) {
        setShops(data.shops); setNoShopsFound(false);
      } else {
        setShops([]); setNoShopsFound(true);
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
      setShops([]); setNoShopsFound(true);
    }
    setLoading(false);
  };

  /* ──────────────────────────────────────────
     JSX
  ────────────────────────────────────────── */
  return (
    <Box sx={{ position: 'relative', p: 2 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{reportTitle}</Typography>
        {userLevel === 1 && shops.length > 0 && (
          <Button variant="contained" color="success" onClick={handleExportCSV}>
            Export to CSV
          </Button>
        )}
      </Box>

      <Filters
        userLevel={userLevel}
        privileges={privileges}
        setFilters={setFilters}
        hasAccess={hasAccess}
      />

      {noShopsFound ? (
        <Typography variant="h6" align="center" sx={{ mt: 3 }}>
          No shops found for the selected filters.
        </Typography>
      ) : (
        <ShopsDataGrid
          shops={shops}
          loading={loading}
          userLevel={userLevel}
          reportType={reportType}
          moduleId={moduleId}
        />
      )}
    </Box>
  );
};

export default ShopReport;
